import React, { useEffect, useMemo, useState } from 'react';
import { Card, Table, Button, ButtonGroup, Row, Col } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import API from '../services/api';
import { bareDate } from '../utils/dates';
import Pager from './Pager';
import Loading from './Loading';
import { billingNote } from '../utils/rentalBilling';

const PAGE_SIZE = 10;
const money = (n) => new Intl.NumberFormat('es-DO', { style: 'currency', currency: 'DOP' }).format(n || 0);
const PERIOD_LABEL = { daily: 'Diario', weekly: 'Semanal', monthly: 'Mensual' };
const RANGES = [[7, '7 días'], [30, '30 días'], [60, '60 días'], [90, '90 días']];

const addDays = (ymd, n) => { const d = new Date(`${ymd}T00:00:00Z`); d.setUTCDate(d.getUTCDate() + n); return d.toISOString().slice(0, 10); };

// "Próximas facturas": every rental invoice due in the chosen window (overdue ones first), from GET /rentals/schedule —
// the same schedule the projection uses. Only a rental's NEXT charge can be invoiced (invoicing advances its
// nextRechargeDate); later ones are shown so staff can see what's coming.
const RentalScheduleTab = () => {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const navigate = useNavigate();

  const load = (range = days) => {
    const today = new Date().toLocaleDateString('en-CA', { timeZone: 'America/Santo_Domingo' });
    API.get('/rentals/schedule', { params: { to: addDays(today, range) } })
      .then(res => setData(res.data))
      .catch(err => setError(err.response?.data?.message || 'Error al cargar las próximas facturas.'));
  };
  useEffect(() => { load(days); }, [days]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setPage(1); }, [days]);

  // Opens Nueva factura filled in from the rental; it is created (and the rental moves on a period) when saved.
  const handleInvoice = (row) => navigate(`/invoices/new?rental=${row.rentalId}`);

  const summary = useMemo(() => {
    const rows = data?.rows || [];
    const sum = (list) => ({ count: list.length, total: list.reduce((s, r) => s + r.total, 0) });
    return { overdue: sum(rows.filter(r => r.overdue)), upcoming: sum(rows.filter(r => !r.overdue)), all: sum(rows) };
  }, [data]);

  // 10 per page, like the Alquileres list; the totals above still count every charge in the range.
  const rows = data?.rows || [];
  const pages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const currentPage = Math.min(page, pages);
  const pagedRows = rows.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  if (!data) return <Card className="shadow-sm p-3">{error ? <div className="alert alert-danger mb-0">{error}</div> : <Loading label="Cargando próximas facturas..." />}</Card>;

  return (
    <>
      <Row className="mb-3">
        <Col md={4} className="mb-3 mb-md-0">
          <Card className="shadow-sm p-3 dashboard-card">
            <div className="text-muted small">Vencidas sin facturar</div>
            <h2 className="mt-2 mb-0" style={{ color: summary.overdue.count ? 'var(--mm-red)' : undefined }}>{money(summary.overdue.total)}</h2>
            <div className="small text-muted mt-1">{summary.overdue.count} {summary.overdue.count === 1 ? 'factura' : 'facturas'}</div>
          </Card>
        </Col>
        <Col md={4} className="mb-3 mb-md-0">
          <Card className="shadow-sm p-3 dashboard-card">
            <div className="text-muted small">Por facturar — próximos {days} días</div>
            <h2 className="mt-2 mb-0" style={{ color: 'var(--mm-cyan)' }}>{money(summary.upcoming.total)}</h2>
            <div className="small text-muted mt-1">{summary.upcoming.count} {summary.upcoming.count === 1 ? 'factura' : 'facturas'}</div>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="shadow-sm p-3 dashboard-card">
            <div className="text-muted small">Total del período (con ITBIS)</div>
            <h2 className="mt-2 mb-0">{money(summary.all.total)}</h2>
            <div className="small text-muted mt-1">hasta el {bareDate(data.to)}</div>
          </Card>
        </Col>
      </Row>

      <Card className="shadow-sm p-3">
        {error && <div className="alert alert-danger">{error}</div>}
        <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
          <div className="text-muted small">
            Una factura por período, cobrada al inicio del período. Solo la próxima de cada alquiler se puede facturar ahora.
          </div>
          <ButtonGroup size="sm">
            {RANGES.map(([n, label]) => (
              <Button key={n} variant={days === n ? 'primary' : 'outline-secondary'} onClick={() => setDays(n)}>{label}</Button>
            ))}
          </ButtonGroup>
        </div>
        <Table hover responsive className="mb-0">
          <thead>
            <tr>
              <th title="Cuándo toca hacer esta factura del alquiler">Facturar</th><th>Alquiler</th><th>Cliente</th><th>Equipo</th><th>Período</th>
              <th className="text-end">Subtotal</th><th className="text-end">Total</th><th></th>
            </tr>
          </thead>
          <tbody>
            {pagedRows.map(r => (
              <tr key={`${r.rentalId}-${r.date}`} style={{ opacity: r.isNext ? 1 : 0.8 }}>
                <td style={{ whiteSpace: 'nowrap' }}>
                  {/* hoy / en N días / atrasado N días — as in the other rental lists (utils/rentalBilling.js) */}
                  {bareDate(r.date)}
                  {billingNote(r.date) && <div className="small" style={{ color: billingNote(r.date).color }}>{billingNote(r.date).text}</div>}
                </td>
                <td>{r.rentalNumber}{r.first && <div className="small text-muted">Primera factura</div>}</td>
                <td>{r.client?.name || '—'}</td>
                <td>{r.equipment || '—'}</td>
                <td>{PERIOD_LABEL[r.billingPeriod] || r.billingPeriod}</td>
                <td className="text-end">{money(r.subtotal)}</td>
                <td className="text-end">{money(r.total)}</td>
                <td className="text-end">
                  {r.isNext && (
                    <Button size="sm" variant={r.overdue || r.date <= data.today ? 'info' : 'outline-primary'} onClick={() => handleInvoice(r)}>
                      {r.first ? 'Facturar' : 'Renovar alquiler'}
                    </Button>
                  )}
                </td>
              </tr>
            ))}
            {data.rows.length === 0 && (
              <tr><td colSpan={8} className="text-muted text-center">No hay facturas de alquiler en este período.</td></tr>
            )}
          </tbody>
        </Table>
        {pages > 1 && (
          <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2">
            <span className="text-muted small">Página {currentPage} de {pages} — {rows.length} factura{rows.length === 1 ? '' : 's'}</span>
            <Pager page={currentPage} pages={pages} onChange={setPage} />
          </div>
        )}
      </Card>
    </>
  );
};

export default RentalScheduleTab;
